import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { isUserRole } from "@examspark/common";
import {
  formatShortDate,
  fromAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  useMutationSetDefaultWorkspaceCalendar,
  useQueryGetDefaultWorkspaceCalendar,
  useQueryListAcademicCalendars,
  useAppContext,
} from "@examspark/common-app";
import {
  Form,
  Label,
  SelectInput,
  Text,
  TextInput,
  Toolbar,
  VSpace,
  toast,
} from "@examspark/common-ui";
import Button, {
  LinkButton,
} from "@examspark/common-ui/dist/components/buttons/Button";
import {
  IconAcceptLine,
  IconPencilLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  AcademicCalendar,
  NonInstructionDate,
  WorkspaceUserRole,
} from "@examspark/types";

import { useFlags } from "../../hooks/useFlags";
import {
  MonthGrid,
  StyledCalendar,
} from "../classroom/planners/ClassroomPlannerCalendar";
import Loading from "../common/Loading";

const Layout = styled.div`
  display: flex;
  gap: 2rem;
`;
const LayoutCalendars = styled.div`
  flex: 3.5;
`;
const LayoutNotes = styled.div`
  flex: 1;
`;

export const NonInstructionalItem = styled.div`
  color: #555;
  display: flex;
  align-items: center;
  gap: 1rem;
`;
export const NonInstructionalLabel = styled.div`
  flex: 1;
`;
export const NonInstructionalDate = styled.div`
  text-align: right;
  color: #888;
`;

type NonInstructionalBadgeProps = {
  type?: NonInstructionDate["type"];
};

const StyledToolbar = styled(Toolbar)`
  align-items: flex-end;
`;

export const NonInstructionalBadge = styled.div<NonInstructionalBadgeProps>`
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 100%;
  background-color: #444;

  ${(props) => {
    switch (props.type) {
      case "HOLIDAY":
        return `background-color: #d7b547;`;
      case "TEACHER_WORKDAY":
        return `background-color: #a06974;`;
      case "ASSESSMENT":
        return `background-color: #e44e4e;`;
      case "OTHER":
        return `background-color: #035E7B;`;
    }
  }}
`;

const WorkspaceCalendar = () => {
  const [context] = useAppContext();
  const flags = useFlags();
  const workspaceId = context.workspaceUser?.workspace?.id;
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  const { data: calendars, isLoading } =
    useQueryListAcademicCalendars(workspaceId);
  const [activeCalendar, setActiveCalendar] = React.useState<
    AcademicCalendar | undefined
  >();

  const { data: defaultCalendar, refetch: refetchDefaultWorkspaceCalendar } =
    useQueryGetDefaultWorkspaceCalendar(workspaceId);
  const setDefaultWorkspaceCalendarMutation =
    useMutationSetDefaultWorkspaceCalendar();

  useEffect(() => {
    if (!calendars || calendars.length === 0) return;
    if (activeCalendar) return;

    if (defaultCalendar) {
      setActiveCalendar(calendars.find((cal) => cal.id === defaultCalendar.id));
    }
  }, [activeCalendar, calendars, defaultCalendar]);

  const canManageCalendars = isUserRole(
    context.workspaceUser,
    WorkspaceUserRole.ContentManager,
  );

  const handleSetDefault = async () => {
    if (!workspaceId) return console.warn("No workspaceId");
    if (!activeCalendar) return console.warn("No activeCalendar");

    try {
      setIsSavingDefault(true);
      await setDefaultWorkspaceCalendarMutation.mutateAsync({
        id: workspaceId,
        sk: "workspace",
        defaultAcademicCalendarId: activeCalendar.id,
      });
      await refetchDefaultWorkspaceCalendar();
      toast.success("Default calendar set");
      setIsSavingDefault(false);
    } catch (error) {
      setIsSavingDefault(false);
    }
  };

  if (isLoading) {
    return <Loading>Loading Academic Calendar...</Loading>;
  }

  return (
    <>
      <StyledToolbar>
        <div style={{ flex: 1 }}>
          <Label label="Calendar">
            <CalendarSelect
              calendars={calendars ?? []}
              activeCalendar={activeCalendar}
              setActiveCalendar={setActiveCalendar}
            />
          </Label>
        </div>
        {canManageCalendars && flags.showCalendarManagement && (
          <LinkButton to="/calendar" icon={<IconPencilLine width="16" />}>
            Manage Calendars
          </LinkButton>
        )}
      </StyledToolbar>

      {activeCalendar && (
        <CalendarTwelveMonth
          cal={activeCalendar}
          onSetDefault={handleSetDefault}
          isSaving={isSavingDefault}
          isDefault={defaultCalendar?.id === activeCalendar.id}
          canManageCalendars={canManageCalendars}
        />
      )}
    </>
  );
};

export const CalendarSelect = ({
  calendars,
  activeCalendar,
  setActiveCalendar,
}: {
  calendars: AcademicCalendar[];
  activeCalendar: AcademicCalendar | undefined;
  setActiveCalendar: (cal: AcademicCalendar | undefined) => void;
}) => {
  const calendarsByWorkspace = useMemo(() => {
    const _calendarsByWorkspace: Record<string, AcademicCalendar[]> = {};
    calendars.forEach((cal) => {
      if (!_calendarsByWorkspace[cal.workspaceId]) {
        _calendarsByWorkspace[cal.workspaceId] = [];
      }
      _calendarsByWorkspace[cal.workspaceId].push(cal);
    });
    return _calendarsByWorkspace;
  }, [calendars]);

  return (
    <SelectInput
      options={Object.entries(calendarsByWorkspace).map(
        ([workspaceId, calendars]) => ({
          label: workspaceId,
          options: calendars.map((cal) => {
            const periodLabel =
              cal.gradingPeriodLength === "6_WEEKS" ? "6 Weeks" : "9 Weeks";
            const label =
              (cal.name ?? "Untitled Calendar") + ` (${periodLabel})`;

            return {
              value: cal.id,
              label,
            };
          }),
        }),
      )}
      value={activeCalendar?.id}
      onChange={(e) =>
        setActiveCalendar(
          calendars.find((cal) => cal.id === e?.value) ?? undefined,
        )
      }
    />
  );
};

export const useMemoGetActiveMonths = (fromDate?: Date) => {
  return useMemo(() => {
    const _months: Date[] = [];

    // this will 'roll over' in july
    const startingDate = fromDate || new Date();
    startingDate.setMonth(startingDate.getMonth() - 6);

    // starting in july, get the next 12 months
    for (let index = 0; index < 12; index++) {
      _months.push(new Date(startingDate.getFullYear(), 6 + index, 1));
    }

    return _months;
  }, [fromDate]);
};

const CalendarTwelveMonth = ({
  cal,
  onSetDefault,
  isDefault,
  isSaving,
  canManageCalendars,
}: {
  cal: AcademicCalendar;
  onSetDefault: () => void;
  isDefault?: boolean;
  isSaving?: boolean;
  canManageCalendars?: boolean;
}) => {
  const monthDates = useMemoGetActiveMonths(
    cal.startAt ? fromAWSDate(cal.startAt) : undefined,
  );

  return (
    <VSpace size="3rem">
      <div>
        <VSpace size="0.5rem">
          <Text.H2>Academic Calendar</Text.H2>
          <Text.Body>
            Displays grading period terms, holidays, assessment dates, and other
            non-instructional dates as set by your district. These important
            dates will also be displayed in your classroom-level calendars.
          </Text.Body>
        </VSpace>
      </div>
      <Layout>
        <LayoutCalendars>
          <StyledCalendar>
            <MonthGrid
              monthDates={monthDates}
              // fake a course calendar
              courseCalendar={{
                calendar: cal,
                units: [],
                additionalNonInstructionDates: [],
              }}
            />
          </StyledCalendar>
        </LayoutCalendars>
        <LayoutNotes>
          <VSpace size="3rem">
            <Form>
              <VSpace size="1.5rem">
                <Text.H3>Calendar Details</Text.H3>
                <Form.FieldList style={{ gap: "1.5rem" }}>
                  <Form.Field label="Calendar Name">
                    <TextInput
                      value={cal.name ?? "Untitled Calendar"}
                      disabled
                    />
                  </Form.Field>
                  <Form.Field label="Grading Period Interval">
                    <TextInput
                      value={cal?.gradingPeriodLength ?? ""}
                      disabled
                    />
                  </Form.Field>
                  <Toolbar>
                    <Toolbar.Flex>
                      {isDefault && (
                        <Toolbar>
                          <div>
                            <IconAcceptLine width="10" />
                          </div>
                          <div>Default Calendar</div>
                        </Toolbar>
                      )}
                    </Toolbar.Flex>
                    <Button
                      title="Set as Default"
                      onClick={onSetDefault}
                      disabled={isDefault || isSaving || !canManageCalendars}
                      isLoading={isSaving}
                    >
                      Set as Default
                    </Button>
                  </Toolbar>
                </Form.FieldList>
              </VSpace>
            </Form>
            <div>
              <VSpace size="2rem">
                <Text.H3>Non-Instructional Days</Text.H3>
                {cal.nonInstructionDates?.map((date) => {
                  return (
                    <NonInstructionalItem key={date.startAt}>
                      <div style={{ width: "12px" }}>
                        <NonInstructionalBadge type={date.type} />
                      </div>
                      <NonInstructionalLabel>
                        {date.label}
                      </NonInstructionalLabel>
                      <NonInstructionalDate>
                        {formatShortDate(fromAWSDate(date.startAt))}
                        {!!date.endAt &&
                          date.endAt !== date.startAt &&
                          ` - ${formatShortDate(fromAWSDate(date.endAt))}`}
                      </NonInstructionalDate>
                    </NonInstructionalItem>
                  );
                })}
              </VSpace>
            </div>
          </VSpace>
        </LayoutNotes>
      </Layout>
    </VSpace>
  );
};

export default WorkspaceCalendar;
